/*
  sanitizeFileName.js
*/

// Truncate code.
// https://github.com/parshap/truncate-utf8-bytes/blob/master/lib/truncate.js
// https://github.com/parshap/utf8-byte-length/blob/master/browser.js

function isHighSurrogate(codePoint) {
  return codePoint >= 0xd800 && codePoint <= 0xdbff;
}

function isLowSurrogate(codePoint) {
  return codePoint >= 0xdc00 && codePoint <= 0xdfff;
}

function getByteLength(string) {
  const charLength = string.length;
  let byteLength = 0;
  let codePoint = null;
  let prevCodePoint = null;
  for (var i = 0; i < charLength; i++) {
    codePoint = string.charCodeAt(i);
    if (isLowSurrogate(codePoint)) {
      if (prevCodePoint != null && isHighSurrogate(prevCodePoint)) {
        byteLength += 1;
      } else {
        byteLength += 3;
      }
    } else if (codePoint <= 0x7f ) {
      byteLength += 1;
    } else if (codePoint >= 0x80 && codePoint <= 0x7ff) {
      byteLength += 2;
    } else if (codePoint >= 0x800 && codePoint <= 0xffff) {
      byteLength += 3;
    }
    prevCodePoint = codePoint;
  }
  return byteLength;
};

function truncate(string, byteLength) {
  const charLength = string.length;
  let curByteLength = 0;
  let codePoint = null;
  let segment = null;
  for (let i = 0; i < charLength; i += 1) {
    codePoint = string.charCodeAt(i);
    segment = string[i];
    if (isHighSurrogate(codePoint) && isLowSurrogate(string.charCodeAt(i + 1))) {
      i += 1;
      segment += string[i];
    }
    curByteLength += getByteLength(segment);
    if (curByteLength === byteLength) {
      return string.slice(0, i + 1);
    } else if (curByteLength > byteLength) {
      return string.slice(0, i - segment.length + 1);
    }
  }
  return string;
};

// Sanitizer code.
// https://github.com/parshap/node-sanitize-filename/blob/master/index.js

const illegalRe = /[\/\?<>\\:\*\|"]/g;
const controlRe = /[\x00-\x1f\x80-\x9f]/g;
const reservedRe = /^\.+$/;
const windowsReservedRe = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;
const windowsTrailingRe = /[\. ]+$/;

function sanitize(input, replacement, extLen = 0) {
  const sanitized = input
    .replace(illegalRe, replacement)
    .replace(controlRe, replacement)
    .replace(reservedRe, replacement)
    .replace(windowsReservedRe, replacement)
    .replace(windowsTrailingRe, replacement);
  return truncate(sanitized, 255 - extLen);
}

/**
 * Sanitizes a filename with an optional extension.
 * If no filename is provided, an empty string is returned.
 * 
 * Based on:
 * https://github.com/parshap/truncate-utf8-bytes/blob/master/lib/truncate.js
 * https://github.com/parshap/node-sanitize-filename/blob/master/index.js
 * 
 * @param {String} name - name of the file.
 * @param {String} ext - optional extension.
 * 
 * @returns {string} resulting filename.
 */
export default function sanitizeFileName(name, ext = '') {

  // Check args.
  if (typeof name !== 'string' || name.length === 0) {
    return '';
  }

  // Parse extension.
  if (typeof ext === 'string' && ext.length > 0) {
    if (!ext.startsWith('.')) {
      ext = `.${ext}`;
    }
  } else {
    ext = '';
  }

  // Remove current extension.
  if (ext.length > 0) {
    const currIdx = name.lastIndexOf('.');
    if (currIdx > 0) {
      name = name.slice(0, currIdx);
    }
  }

  // Sanitize the name, leaving room for the optional extension (assumed to be ASCII).
  const replacement = '_';
  name = sanitize(name, replacement, ext.length);
  if (replacement !== '') {
    name = sanitize(name, '');
  }

  // Return with extension.
  return `${name}${ext}`;

}
